import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Textarea } from "@v2/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { PLAYER_ROLES, playerRoleLabel } from "../../lib/labels";
import type { Player } from "../../types";

export function PlayerEditDialog({
  open,
  onOpenChange,
  player,
  teamId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player: Player;
  teamId: string;
}) {
  const { t } = useTranslation("players");
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(player.first_name || "");
  const [lastName, setLastName] = useState(player.last_name || "");
  const [shirtNumber, setShirtNumber] = useState<string>(
    player.shirt_number != null ? String(player.shirt_number) : ""
  );
  const [roleCode, setRoleCode] = useState(player.role_code || "");
  const [eaSportsId, setEaSportsId] = useState(player.ea_sports_id || "");
  const [eaAccountHandle, setEaAccountHandle] = useState(player.ea_account_handle || "");
  const [email, setEmail] = useState(player.email || "");
  const [phone, setPhone] = useState(player.phone || "");
  const [status, setStatus] = useState(player.status || "active");
  const [isGuest, setIsGuest] = useState(!!player.is_guest);
  const [tryoutNotes, setTryoutNotes] = useState(player.tryout_notes || "");
  const [error, setError] = useState<string | null>(null);

  // Resync stato all'apertura quando il player cambia.
  useEffect(() => {
    if (!open) return;
    setFirstName(player.first_name || "");
    setLastName(player.last_name || "");
    setShirtNumber(player.shirt_number != null ? String(player.shirt_number) : "");
    setRoleCode(player.role_code || "");
    setEaSportsId(player.ea_sports_id || "");
    setEaAccountHandle(player.ea_account_handle || "");
    setEmail(player.email || "");
    setPhone(player.phone || "");
    setStatus(player.status || "active");
    setIsGuest(!!player.is_guest);
    setTryoutNotes(player.tryout_notes || "");
    setError(null);
    // Intenzionale: re-inizializza il form solo all'apertura del dialog o al
    // cambio dell'entita' (player.id). Listare tutti i campi farebbe perdere
    // le modifiche locali a ogni refetch della rosa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, player.id]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = {
        first_name: firstName.trim() || player.first_name,
        last_name: lastName.trim() || "",
        shirt_number: shirtNumber.trim() ? Number(shirtNumber) : null,
        role_code: roleCode.trim() || null,
        ea_sports_id: eaSportsId.trim() || null,
        ea_account_handle: eaAccountHandle.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        status,
        is_guest: isGuest,
        tryout_notes: isGuest ? (tryoutNotes.trim() || null) : null,
      };
      const { error: updateError } = await supabaseV2
        .from("players")
        .update(payload)
        .eq("id", player.id);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["ea-roster", teamId] });
      queryClient.invalidateQueries({ queryKey: ["player-linked-email", player.id] });
      onOpenChange(false);
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("edit.title")}</DialogTitle>
          <DialogDescription>
            {t("edit.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.firstName")}</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.lastName")}</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.number")}</Label>
              <Input
                value={shirtNumber}
                onChange={(e) => setShirtNumber(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-8 text-sm font-mono"
                placeholder="-"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.role")}</Label>
              <Select value={roleCode || "__none"} onValueChange={(v) => setRoleCode(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder={t("edit.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("edit.roleNone")}</SelectItem>
                  {PLAYER_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="font-mono mr-2">{r}</span>
                      <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.eaSportsId")}</Label>
              <Input
                value={eaSportsId}
                onChange={(e) => setEaSportsId(e.target.value)}
                className="h-8 text-sm font-mono"
                placeholder={t("edit.eaSportsIdPh")}
              />
              <p className="text-[10px] text-muted-foreground">
                {t("edit.eaSportsIdHint")}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.proClubsHandle")}</Label>
              <Input
                value={eaAccountHandle}
                onChange={(e) => setEaAccountHandle(e.target.value)}
                className="h-8 text-sm font-mono"
                placeholder={t("edit.proClubsHandlePh")}
              />
              <p className="text-[10px] text-muted-foreground">
                {t("edit.proClubsHandleHint")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-8 text-sm"
                placeholder="-"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.phone")}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.status")}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("edit.statusActive")}</SelectItem>
                  <SelectItem value="injured">{t("edit.statusInjured")}</SelectItem>
                  <SelectItem value="inactive">{t("edit.statusInactive")}</SelectItem>
                  <SelectItem value="suspended">{t("edit.statusSuspended")}</SelectItem>
                  <SelectItem value="archived">{t("edit.statusArchived")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer h-8">
                <input
                  type="checkbox"
                  checked={isGuest}
                  onChange={(e) => setIsGuest(e.target.checked)}
                  className="rounded"
                />
                {t("edit.guestPlayer")}
              </label>
            </div>
          </div>
          {isGuest && (
            <div className="space-y-1">
              <Label className="text-[11px]">{t("edit.tryoutNotes")}</Label>
              <Textarea
                value={tryoutNotes}
                onChange={(e) => setTryoutNotes(e.target.value)}
                rows={3}
                placeholder={t("edit.tryoutNotesPh")}
                className="text-sm"
              />
            </div>
          )}
          {error && (
            <div className="rounded-md px-2 py-1 text-xs bg-rose-50 text-rose-700 border border-rose-200">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            {t("edit.cancel")}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? t("edit.saving") : t("edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
